import { fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { RootState } from 'types';
import { BaseQueryApi } from '@reduxjs/toolkit/query';
import { toast } from 'app/components/ui/use-toast';


const baseUrl = 'https://api-loop-c.appening.xyz'; // Live

const imageBaseUrl = baseUrl + '/uploads/';

const defaultHeaders = {
  Accept: 'application/json',
  'Content-Type': 'application/json',
};
  // tokem from session storage
  const token = sessionStorage.getItem('api_token');

const storeTokenInSession = (token: string) => {
  if (token) {
    localStorage.setItem('api_token', token);
  }
};

/**
 * Handles unauthorized (401) responses by:
 * 1. Showing a toast notification to the user
 * 2. Clearing session storage and localStorage
 * 3. Redirecting to the login page after a short delay
 */
const handleUnauthorized = () => {
  // Show toast message
  toast({
    title: "Session Expired",
    description: "Redirecting to login...",
    variant: "destructive",
  });
  
  // Clear session storage
  // localStorage.removeItem('api_token');
  // localStorage.removeItem('unauthorize');
  
  // Navigate to signin page after a short delay to show the toast
 if (typeof window !== 'undefined') {
    setTimeout(() => {
      window.location.href = '/login';
    }, 1500); // 1.5 second delay to show the toast
  } 
};

// Export for use in other parts of the application
export { handleUnauthorized };

const prepareHeaders = (headers: Headers, { getState }: Pick<BaseQueryApi, 'getState'>) => {
  // First try to get token from Redux state
  const token = (getState() as RootState)?.global?.user?.api_token;

  // If token exists in Redux, store it in session storage
  if (token) {
    storeTokenInSession(token);
  }
  
  // Get token from session storage as fallback
  const sessionToken = localStorage.getItem('api_token');
  const tokenToUse = token || sessionToken 



  
  if (!headers.has('Authorization')) {
    headers.set('Authorization', `Bearer ${tokenToUse}`);
  }
  return headers;
};




const baseQueryConfig = fetchBaseQuery({
  baseUrl,
  headers: defaultHeaders,
  prepareHeaders,
});

// Enhanced baseQuery with unauthorized handling
export const baseQuery = async (args: any, api: any, extraOptions: any) => {
  const result = await baseQueryConfig(args, api, extraOptions);
 
  
  // Handle unauthorized responses
  if (result.error && (result.error.status === 401 || (result.error as any).statusText === 'Unauthorized')) {
    handleUnauthorized();
  }
  
  return result;
};

// export const formatErrors = (errors: any) => {
//   return errors?.response || 'Something went wrong';
// };
export const formatErrors = (errors: any) => {
 
  console.log('errors', errors);
  
  // Handle 401 Unauthorized
  if (errors?.status === 401 || errors?.statusText === 'Unauthorized') {
    handleUnauthorized();
    return 'Your session has expired. Please sign in again.';
  }
  
  if (
    Array.isArray(errors?.data?.messages) &&
    errors?.data?.messages?.length > 0
  ) {
    return errors?.data?.messages[0];
  } else if (errors?.status === 500) {
    return (
      errors?.statusText || errors?.data?.statusText || 'Internal Server Error'
    );
  } else if (errors?.status === 400) {
    return errors?.messages.length > 0 ? errors?.messages : errors?.message[0];
  } else if (errors.response === 'Unauthorized') {
    handleUnauthorized();
    return 'Your session has expired. Please sign in again.';
  } else {
    return 'Something went wrong';
  }
};


export const transformPaginatedResponse = data => data?.response;

export const HTTP_METHODS = {
  GET: 'GET',
  POST: 'POST',
  PUT: 'PUT',
  DELETE: 'DELETE',
  PATCH: 'PATCH',
};

export const endpoints = {
  baseUrl,
  imageBaseUrl,
  apiBaseUrl: baseUrl + '/api',
  sendOtp: {
    url: `/send-otp`,
    method: HTTP_METHODS.POST,
  },
  // Appointment
  appointmentCreate: {
    url: `/api/appointments`,
    method: HTTP_METHODS.POST,
  },
  appointmentGetById: {
    url: `/api/appointments/:appointmentId`,
    method: HTTP_METHODS.GET,
  },
  appointmentListByProvider: {
    url: `/api/appointments?provider=:providerId`,
    method: HTTP_METHODS.GET,
  },
  appointmentUpdate: {
    url: `/api/appointments/:appointmentId`,
    method: HTTP_METHODS.PUT,
  },
  appointmentStatusChange: {
    url: `/api/appointments/:appointmentId`,
    method: HTTP_METHODS.PUT,
  },
  appointmentDelete: {
    url: `/api/appointments/:appointmentId`,
    method: HTTP_METHODS.DELETE,
  },
  appointmentSummary: {
    url: `/api/appointments/summary`,
    method: HTTP_METHODS.GET,
  },
  // Referral
  referralCreate: {
    url: `/api/referrals`,
    method: HTTP_METHODS.POST,
  },
  referralGetById: {
    url: `/api/referrals/:referralId`,
    method: HTTP_METHODS.GET,
  },
  referralList: {
    url: `/api/referrals`,
    method: HTTP_METHODS.GET,
  },
  referralUpdate: {
    url: `/api/referrals/:referralId`,
    method: HTTP_METHODS.PUT,
  },
  referralDelete: {
    url: `/api/referrals/:referralId`,
    method: HTTP_METHODS.DELETE,
  },
  // Note
  noteAddToAppointment: {
    url: `/api/appointments/:appointmentId/notes`,
    method: HTTP_METHODS.POST,
  },
  noteAddToReferral: {
    url: `/api/referrals/:referralId/notes`,
    method: HTTP_METHODS.POST,
  },
  noteListByAppointment: {
    url: `/api/appointments/:appointmentId/notes`,
    method: HTTP_METHODS.GET,
  },
  noteListByReferral: {
    url: `/api/referrals/:referralId/notes`,
    method: HTTP_METHODS.GET,
  },
  noteUpdate: {
    url: `/api/notes/:noteId`,
    method: HTTP_METHODS.PUT,
  },
  noteDelete: {
    url: `/api/notes/:noteId`,
    method: HTTP_METHODS.DELETE,
  },
  noteView: {
    url: `/api/notes/:noteId`,
    method: HTTP_METHODS.GET,
  },
  practiceInfo: {
    url: `/api/practices/profile`,
    method: HTTP_METHODS.GET,
  },
  addPracticeInfo: {
    url: `/api/practices`,
    method: HTTP_METHODS.POST,
  },
  // Notification
  notificationList: {
    url: `/api/notifications`,
    method: HTTP_METHODS.GET,
  },
  notificationMarkAsRead: {
    url: `/api/notifications/:notificationId/mark-read`,
    method: HTTP_METHODS.POST,
  },
  notificationMarkAllAsRead: {
    url: `/api/notifications/mark-multiple-read`,
    method: HTTP_METHODS.POST,
  },
  notificationDelete: {
    url: `/api/notifications/:notificationId`,
    method: HTTP_METHODS.DELETE,
  },
  // Socket
  socketConnect: {
    url: `ws://localhost:3000/socket.io/?EIO=4&transport=websocket`,
    method: HTTP_METHODS.GET,
  },
  socketSendTestNotification: {
    url: `/api/test/notify`,
    method: HTTP_METHODS.POST,
  },
  // Register
  registerInitiate: {
    url: `/auth/register/initiate`,
    method: HTTP_METHODS.POST,
  },
  registerVerifyCode: {
    url: `/auth/register/verify-code`,
    method: HTTP_METHODS.POST,
  },
  registerSetPassword: {
    url: `/auth/register/set-password`,
    method: HTTP_METHODS.POST,
  },
  providerProfileSetup: {
    url: `/api/providers`,
    method: HTTP_METHODS.POST,
  },
  // Auth
  login: {
    url: `/auth/login`,
    method: HTTP_METHODS.POST,
  },
  logout: {
    url: `/auth/logout`,
    method: HTTP_METHODS.POST,
  },
  verifyOtpLogin: {
    url: `/auth/verify-code`,
    method: HTTP_METHODS.POST,
  },
  resendVerificationCode:{
    url: `/auth/resend-verification-code`,
    method: HTTP_METHODS.POST,
  },
  resendVerificationCodeRegister: {
    url: `/auth/register/resend-code`,
    method: HTTP_METHODS.POST,
  },
  forgotPassword: {
    url: `/auth/user/forgot-password`,
    method: HTTP_METHODS.POST,
  },
  resetPassword: {
    url: `/auth/user/reset-password`,
    method: HTTP_METHODS.POST,
  },
  // Webhook
  webhookAppointment: {
    url: `/api/webhooks/appointment`,
    method: HTTP_METHODS.POST,
  },
  webhookReferral: {
    url: `/api/webhooks/referral`,
    method: HTTP_METHODS.POST,
  },
  // User Profile
  userProfile: {
    url: `/api/providers/profile`,
    method: HTTP_METHODS.GET,
  },
  // Help Center
  reportIssue: {
    url: `/api/providers/report-issue`,
    method: HTTP_METHODS.POST,
  },
  submitFeedback: {
    url: `/api/providers/feedback`,
    method: HTTP_METHODS.POST,
  },
  submitOtherHelpRequest: {
    url: `/api/providers/other-help-request`,
    method: HTTP_METHODS.POST,
  },
  // Insight Overview
  insightOverview: {
    url: `/api/insight-chart`,
    method: HTTP_METHODS.GET,
  },
  paidCampaigns: {
    url: `/api/insight-paid-campaigns`,
    method: HTTP_METHODS.GET,
  },
  newPatientsCharts:{
    url: `/api/insight-leads-chart`,
    method: HTTP_METHODS.GET,
  },
  websiteHealthCheckup: {
    url: `/api/insight-website-traffic`,
    method: HTTP_METHODS.GET,
  },
  googleRankingsByKeywords: {
    url: `/api/insight-google-rankings`,
    method: HTTP_METHODS.GET,
  },
  keywordOpportunities:{
    url: `/api/insight-keyword-opportunities`,
    method: HTTP_METHODS.GET,
  },
  googleReviews:{
    url: `/api/google-reviews/dashboard`,
    method: HTTP_METHODS.GET,
  },
  googleReviewUnreplied:{
    url: `/api/google-reviews`,
    method: HTTP_METHODS.GET,
  },
  googleReviewReply:{
    url: `api/google-reviews/reply`,
    method: HTTP_METHODS.POST,
  },
  howPeopleFindUs:{
    url: `/api/insight-appointment-sources`,
    method: HTTP_METHODS.GET,
  },
};
